import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import { sanitizeNextPath } from '../lib/authRedirects';
import { getSupplierPrimaryRoute } from '../lib/supplierOnboarding';
import { logClaimEvent, installClaimErrorCapture } from '../lib/claimFunnel';
import BrandedLoading from '../components/BrandedLoading';
import { track } from '../lib/growthTracker';

const COPY = {
  ar: {
    loadingTitle: 'جارٍ تأكيد الدخول…',
    loadingBody: 'نجهّز حسابك الآن ونحوّلك إلى الوجهة الصحيحة.',
    errorTitle: 'تعذر إكمال التحقق',
    errorBody: 'رابط التحقق غير صالح أو منتهي. حاول تسجيل الدخول مرة أخرى أو اطلب رسالة جديدة.',
    backToSupplier: 'العودة إلى دخول المورد',
    backToBuyer: 'العودة إلى الدخول',
    home: 'العودة للرئيسية',
  },
  en: {
    loadingTitle: 'Finalizing your sign-in…',
    loadingBody: 'We are preparing your account and sending you to the right place.',
    errorTitle: 'We could not complete verification',
    errorBody: 'This confirmation link is invalid or expired. Try signing in again or request a fresh email.',
    backToSupplier: 'Back to supplier sign in',
    backToBuyer: 'Back to sign in',
    home: 'Back to home',
  },
  zh: {
    loadingTitle: '正在完成登录验证…',
    loadingBody: '系统正在准备您的账户，并将您带到正确页面。',
    errorTitle: '无法完成邮箱验证',
    errorBody: '该确认链接无效或已过期。请重新登录或请求新的确认邮件。',
    backToSupplier: '返回供应商登录',
    backToBuyer: '返回登录',
    home: '返回首页',
  },
};

function getHashParams(hash = '') {
  return new URLSearchParams((hash || '').replace(/^#/, ''));
}

function readAuthError(searchParams, hashParams) {
  return searchParams.get('error_description')
    || searchParams.get('error')
    || hashParams.get('error_description')
    || hashParams.get('error')
    || '';
}

export default function AuthCallback({ user, profile, lang }) {
  const nav = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const t = COPY[lang] || COPY.en;

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hashParams = useMemo(() => getHashParams(location.hash), [location.hash]);
  const nextPath = useMemo(() => sanitizeNextPath(searchParams.get('next') || hashParams.get('next') || '/dashboard'), [searchParams, hashParams]);
  const requestedRole = searchParams.get('role') || hashParams.get('role') || '';
  const authType = searchParams.get('type') || hashParams.get('type') || '';
  // A pending factory claim carried through OAuth. Accepted ONLY when `next` is an
  // internal /claim/<slug> path (no open redirect); the bare slug is passed to the
  // claim_factory_by_slug RPC, never used as a redirect target.
  const claimSlug = useMemo(() => {
    const raw = searchParams.get('next') || hashParams.get('next') || '';
    const m = /^\/claim\/([A-Za-z0-9._-]+)\/?$/.exec(raw);
    return m ? m[1] : null;
  }, [searchParams, hashParams]);

  useEffect(() => {
    let active = true;
    const removeErrorCapture = installClaimErrorCapture(claimSlug || 'callback', 'callback');

    const finalizeAuth = async () => {
      const incomingError = readAuthError(searchParams, hashParams);
      if (incomingError) {
        if (!active) return;
        logClaimEvent(claimSlug, 'auth_link_error', { message: String(incomingError).slice(0, 200) });
        setStatus('error');
        setErrorMessage(incomingError);
        return;
      }

      try {
        const code = searchParams.get('code');
        const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');

        if (code) {
          const { error } = await sb.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (tokenHash && authType) {
          const { error } = await sb.auth.verifyOtp({ token_hash: tokenHash, type: authType });
          if (error) throw error;
        } else if (accessToken && refreshToken) {
          const { error } = await sb.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        } else {
          await new Promise((resolve) => setTimeout(resolve, 350));
        }

        const { data: { session } } = await sb.auth.getSession();
        if (!session?.user) {
          throw new Error('Missing authenticated session after callback.');
        }

        if (!active) return;
        setStatus('ready');
      } catch (error) {
        if (!active) return;
        logClaimEvent(claimSlug, 'auth_callback_error', { message: String(error?.message || error).slice(0, 200) });
        setStatus('error');
        setErrorMessage(error?.message || 'Auth callback failed.');
      }
    };

    finalizeAuth();
    return () => { active = false; removeErrorCapture(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, hashParams, authType, claimSlug]);

  const promotedRef = useRef(false);
  const linkedRef = useRef(false);
  // Auto-link a supplier account to its pre-existing factory row (by email/phone).
  // Idempotent + best-effort; server-side match, so a no-op when there's nothing
  // to link. Runs once per auth event, BEFORE we route in, so a matched factory
  // lands on its already-seeded profile/products.
  const linkFactoryOnce = async () => {
    if (linkedRef.current) return;
    linkedRef.current = true;
    try { await sb.rpc('link_account_to_factory'); } catch { /* best-effort */ }
  };

  useEffect(() => {
    if (status !== 'ready' || !user) return;

    // A pending factory claim (a /claim/<slug> carried through OAuth) takes
    // precedence over every role check. It is gated on `claimSlug` — NOT on
    // requestedRole — so the claim still runs when the OAuth round-trip drops the
    // `role` param but keeps `next`, and even when the account was already
    // promoted to supplier on a prior attempt (which would otherwise short-circuit
    // into the by-email/phone link that never matches this factory). Running it
    // first is safe: claim_factory_by_slug binds linked_supplier_id, seeds, and
    // sets the supplier role itself, and is idempotent (no-op when this account
    // already owns the factory). Surface its error instead of swallowing it.
    if (claimSlug && !promotedRef.current) {
      promotedRef.current = true;
      logClaimEvent(claimSlug, 'verify_returned', { method: 'oauth' });
      (async () => {
        logClaimEvent(claimSlug, 'claim_attempted', { via: 'callback' });
        try {
          await sb.rpc('claim_factory_by_slug', { p_slug: claimSlug });
          logClaimEvent(claimSlug, 'claim_succeeded', { via: 'callback' });
        } catch (e) {
          logClaimEvent(claimSlug, 'claim_failed', { via: 'callback', message: String(e?.message || e).slice(0, 200) });
          setStatus('error');
          setErrorMessage(e?.message || 'Could not confirm factory ownership.');
          return;
        }
        // Full load, not nav(): the profile was read on OAuth sign-in (as a buyer —
        // Google cannot carry role metadata) BEFORE the claim set the supplier role,
        // and App.js ignores TOKEN_REFRESHED / same-user SIGNED_IN (App.js:529,547).
        window.location.assign('/dashboard');
      })();
      return;
    }

    if (profile?.role === 'supplier') {
      (async () => {
        await linkFactoryOnce();
        nav(getSupplierPrimaryRoute(profile, user), { replace: true });
      })();
      return;
    }

    // Social supplier signup (role=supplier on the callback URL, no claim slug):
    // the OAuth account is created as buyer/none, so promote it to supplier via the
    // DB-guarded claim_supplier_role RPC, then auto-link to its factory, then route in.
    if (requestedRole === 'supplier' && !promotedRef.current) {
      promotedRef.current = true;
      (async () => {
        try { await sb.rpc('claim_supplier_role'); } catch { /* best-effort */ }
        await linkFactoryOnce();
        nav('/dashboard', { replace: true });
      })();
      return;
    }

    if (profile) {
      nav(nextPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user, profile, nextPath, nav, requestedRole, claimSlug]);

  // Growth OS (step 7): a Google return completes a sign-up (account made in
  // the last 10 minutes) or a sign-in. Once per callback; never affects routing.
  const growthNoted = useRef(false);
  useEffect(() => {
    if (status !== 'ready' || !user || growthNoted.current) return;
    if (user.app_metadata?.provider !== 'google') return;
    growthNoted.current = true;
    // A new account's signup_completed is the server's (growth_link_identity, on sign-in).
    const isNew = Date.now() - Date.parse(user.created_at || 0) < 10 * 60 * 1000;
    if (!isNew) track('login_completed', { method: 'google' });
  }, [status, user, requestedRole]);

  // Fallback timeout — if profile is still null after 5 seconds, redirect to dashboard anyway
  useEffect(() => {
    if (!user || profile) return; // If profile exists, no timeout needed
    const timeout = setTimeout(() => {
      // Profile still null after 5 seconds — /dashboard routes by role (there is no
      // /dashboard/supplier route; that path 404s).
      nav('/dashboard', { replace: true });
    }, 5000);
    return () => clearTimeout(timeout);
  }, [user, profile, nav, requestedRole]);

  const loginTarget = requestedRole === 'supplier' ? '/login/supplier' : '/login';

  if (status !== 'error') {
    return (
      <BrandedLoading
        lang={lang}
        tag="MAABAR AUTH"
        title={t.loadingTitle}
        body={t.loadingBody}
        tone="app"
        fullscreen
      />
    );
  }

  return (
    <div style={{
      minHeight: 'var(--app-dvh)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      padding: 24,
      color: 'var(--text-primary)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 520,
        borderRadius: 24,
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-raised)',
        padding: '36px 30px',
        textAlign: lang === 'ar' ? 'right' : 'left',
      }}>
        <p style={{
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          margin: '0 0 12px',
        }}>
          MAABAR AUTH
        </p>

        <h1 style={{
          margin: '0 0 12px',
          fontSize: lang === 'ar' ? 30 : 32,
          fontWeight: 300,
          lineHeight: 1.15,
          fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)',
        }}>
          {status === 'error' ? t.errorTitle : t.loadingTitle}
        </h1>

        <p style={{
          margin: 0,
          fontSize: 14,
          lineHeight: 1.8,
          color: 'var(--text-secondary)',
          fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)',
        }}>
          {status === 'error' ? t.errorBody : t.loadingBody}
        </p>

        {status === 'error' ? (
          <>
            {errorMessage ? (
              <p style={{
                margin: '18px 0 0',
                fontSize: 12,
                lineHeight: 1.7,
                color: '#d9a5a5',
                wordBreak: 'break-word',
                fontFamily: 'var(--font-sans)',
              }}>
                {errorMessage}
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 22 }}>
              <button
                onClick={() => nav(loginTarget, { replace: true })}
                style={{
                  background: 'var(--text-primary)',
                  color: 'var(--bg-base)',
                  border: 'none',
                  borderRadius: 14,
                  minHeight: 44,
                  padding: '11px 18px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {requestedRole === 'supplier' ? t.backToSupplier : t.backToBuyer}
              </button>
              <button
                onClick={() => nav('/', { replace: true })}
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 14,
                  minHeight: 44,
                  padding: '11px 18px',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                {t.home}
              </button>
            </div>
          </>
        ) : (
          <div style={{
            width: 40,
            height: 2,
            borderRadius: 999,
            background: 'rgba(0,0,0,0.10)',
            marginTop: 24,
            overflow: 'hidden',
            position: 'relative',
          }}>
            <span style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              transformOrigin: 'left center',
              animation: 'authCallbackPulse 1.2s ease-in-out infinite',
            }} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes authCallbackPulse {
          0%, 100% { transform: scaleX(0.25); opacity: 0.4; }
          50% { transform: scaleX(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
